import { Button } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';


import { DynamicSchema, PaymentEstimateObject, RequestItemsSchema } from '@liscio/api';
import { useApiClient, useRequestStore } from '@liscio/common';
import { Card, Divider, Icon, Skeleton, Stack, styled, Typography } from '@liscio/ui';
import { formatStringDate } from '@liscio/utils';

import { EditableQuarterInfo } from '../EditableQuarterInfo/EditableQuarterInfo';
import { QUARTERS } from '../EditableQuarterInfo/quarters';
import { getUrlPath } from '../utils';

const StyledCard = styled(Card)(({ theme }) => ({
  paddingRight: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

const QuartersContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

export type EstimatedPaymentsContainerProps = {
  paymentEstimatesItem: RequestItemsSchema;
};

export const EstimatedPaymentsContainer = ({
  paymentEstimatesItem,
}: EstimatedPaymentsContainerProps) => {
  const apiClient = useApiClient();
  const { request } = useRequestStore();
  const queryClient = useQueryClient();

  const readUrl = (paymentEstimatesItem.dependency?.object as DynamicSchema | undefined)?.read_url;
  const urlPath = getUrlPath(readUrl);

  const { data, isLoading } = useQuery<PaymentEstimateObject[]>({
    queryKey: ['paymentEstimates', urlPath],
    queryFn: () => {
      if (!urlPath) {
        throw new Error('Read URL is required');
      }
      return apiClient.client.get<PaymentEstimateObject[]>(urlPath).then((res: AxiosResponse<PaymentEstimateObject[]>) => res.data);
    },
    enabled: !!urlPath,
  });

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['paymentEstimates', urlPath] });
  };

  if (isLoading) {
    return (
      <Stack>
        <Skeleton width="100%" />
        <Skeleton width="100%" />
      </Stack>
    );
  }

  const paymentEstimates = data || [];

  if (paymentEstimates.length === 0) {
    return null;
  }

  return (
    <Stack gap={1}>
      <Typography variant="h5" fontWeight={600}>
        {paymentEstimates[0]?.q1_due_date
          ? `${formatStringDate({
              dateString: paymentEstimates[0].q1_due_date,
              defaultPlaceholder: '',
              outputFormat: 'y',
            })} ESTIMATED PAYMENTS`
          : 'ESTIMATED PAYMENTS'}
      </Typography>
      <StyledCard elevation={0}>
        {paymentEstimates.map((paymentEstimate, estimateIndex) => {
          const isLastEstimate = estimateIndex === paymentEstimates.length - 1;
          return (
            <Stack key={estimateIndex} gap={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={600}>
                  {paymentEstimate.jurisdiction}
                </Typography>
                {paymentEstimate.payment_url && (
                  <Button
                    variant="text"
                    size="small"
                    endIcon={<Icon icon="chevronRight" />}
                    onClick={() =>
                      window.open(paymentEstimate.payment_url!, '_blank')
                    }
                  >
                    Pay
                  </Button>
                )}
              </Stack>
              <QuartersContainer>
                {QUARTERS.map(({ key, title, amountKey, dateKey }) => (
                  <EditableQuarterInfo
                    key={key}
                    title={title}
                    amountInCents={paymentEstimate[amountKey]}
                    dueDate={paymentEstimate[dateKey]}
                    paymentEstimate={paymentEstimate}
                    quarter={key}
                    requestStatus={request?.status}
                    onSuccess={handleSuccess}
                  />
                ))}
              </QuartersContainer>
              {!isLastEstimate && <Divider />}
            </Stack>
          );
        })}
      </StyledCard>
    </Stack>
  );
};
